import styled, {css} from 'styled-components';
import {getKeyframes} from '../utils/getKeyframes';
import {getSize, SizeTypes} from '../utils/getSize';
import {
    cardBackground,
    borderColor,
    borderRadius,
    cardShadow,
    fontFamily,
    inputPadding,
    inputSize, media,
    disabledForeground,
    primaryColor,
    primaryForeground,
    primaryHover,
} from '../utils/variables';

interface ImageProps {
    padding?: number;
    rotate?: number;
    background?: string;
    isLoading?: boolean;
    heightSize?: string;
}

interface ButtonProps {
    extent?: SizeTypes;
    isLoading?: boolean;
    heightSize?: string;
    background?: string;
    dropdown?: boolean;
    hideOnDesktop?: boolean;
}

interface LinkProps {
    extent?: SizeTypes;
    isLoading?: boolean;
    heightSize?: string;
    background?: string;
    dropdown?: boolean;
}

const buttonBase = css<ButtonProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: ${fontFamily};
    font-weight: 600;
    transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
    cursor: pointer;
    border-radius: ${borderRadius};
    padding: ${inputPadding};
    line-height: 1.2;

    :disabled {
        background: ${disabledForeground} !important;
        cursor: not-allowed;
        box-shadow: none;

        :hover {
            transform: none;
            box-shadow: none;
        }
    }
`;

const loadingSpinner = css`
    position: relative;
    color: transparent !important;
    cursor: wait;
    pointer-events: none;

    &::before {
        content: '';
        position: absolute;
        left: 50%;
        top: 50%;
        width: 14px;
        height: 14px;
        margin: -7px 0 0 -7px;
        border-radius: 50%;
        border: 2px solid transparent;
        border-top-color: ${primaryForeground};
        animation: infiniteRotate 1s infinite linear;
        box-sizing: border-box;
    }
`;

const loadingButtonStyles = css<ButtonProps>`
    ${loadingSpinner}
    background: ${({background}) => background ?? primaryColor} !important;
    opacity: 0.92;
`;

export const Link = styled.a<LinkProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: ${inputPadding};
    color: ${primaryColor};
    font-weight: 600;
    line-height: 1.2;
    width: ${({extent}) =>
            extent ? `${getSize(extent, inputSize)}` : 'unset'};
    transition: color 0.2s ease;

    :hover {
        color: ${primaryHover};
    }

    :active {
        opacity: 0.85;
    }

    :disabled {
        color: ${disabledForeground};
        cursor: not-allowed;
    }

    ${({isLoading}) => isLoading ? loadingSpinner : ''}
    height: ${({heightSize}) => (heightSize ? `${heightSize}` : 'unset')};
    ${getKeyframes('rotate')}
`;

export const Button = styled.button<ButtonProps>`
    ${buttonBase}
    border: none;
    background: ${({background}) => background ? background : primaryColor};
    color: ${primaryForeground};
    width: ${({extent}) =>
            extent ? `${getSize(extent, inputSize)}` : 'unset'};

    :hover:not(:disabled) {
        background: ${({background}) => background ? background : primaryHover};
        box-shadow: 0 2px 8px rgba(43, 127, 255, 0.25);
    }

    :active:not(:disabled) {
        transform: translateY(1px);
        box-shadow: none;
    }

    :disabled {
        background: ${disabledForeground} !important;
        cursor: not-allowed;
        box-shadow: none;

        :hover {
            transform: none;
            box-shadow: none;
        }
    }

    ${({isLoading}) => isLoading ? loadingButtonStyles : ''}
    height: ${({heightSize}) => (heightSize ? `${heightSize}` : 'unset')};
    ${getKeyframes('rotate')}

    ${media.mobile} {
        font-size: 12px;
    }

    ${media.tablet} {
        font-size: 12px;
    }
`;

export const FilterButton = styled.button<ButtonProps>`
    ${buttonBase}
    border: none;
    background: ${({background}) => background ? background : primaryColor};
    color: ${primaryForeground};
    font-size: 11px;
    width: 80px;
    height: 30px;

    :hover:not(:disabled) {
        background: ${({background}) => background ? background : primaryHover};
    }

    ${getKeyframes('rotate')}

    ${({hideOnDesktop = true}) =>
            hideOnDesktop
                    ? css`
                        ${media.desktop} {
                            display: none;
                        }
                    `
                    : ``}
    @media (min-width: 1201px) {
        display: none;
    }
`;

export const ButtonImgArrowIconDiv = styled.div<ImageProps>`
    width: 42px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: unset;
    cursor: pointer;
    transition: background-color 0.2s ease;
    transform: rotate(90deg);
    background: transparent;
    border-top-right-radius: ${borderRadius};
    border-top-left-radius: ${borderRadius};

    :hover {
        background: ${({background}) => (background ? background : primaryColor)};
    }

    :disabled {
        background: ${disabledForeground};
        cursor: not-allowed;
    }
`;

export const ButtonImgArrowIcon = styled.img<ImageProps>`
    width: 25px;
    height: 25px;
`;

export const ButtonCreateExcel = styled.button<ButtonProps>`
    width: 120px;
    height: 40px;
    font-size: 14px;
    font-weight: 600;
    font-family: ${fontFamily};
    color: ${primaryForeground};
    background-color: ${primaryColor};
    border-radius: ${borderRadius};
    border: 1px solid ${borderColor};
    cursor: pointer;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;

    :hover:not(:disabled) {
        background-color: ${primaryHover};
        box-shadow: 0 2px 8px rgba(43, 127, 255, 0.2);
    }

    :active:not(:disabled) {
        transform: translateY(1px);
    }

    ${media.mobile} {
        font-size: 11px;
        width: 80px;
        height: 30px;
    }

    ${media.tablet} {
        font-size: 11px;
        width: 80px;
        height: 30px;
    }

    padding: 4px;
    text-align: center;
    justify-content: center;

    :disabled {
        background: ${disabledForeground};
        cursor: not-allowed;
    }

    ${({isLoading}) => isLoading ? loadingButtonStyles : ''}
    ${getKeyframes('rotate')}
`;

export const ButtonCreateExcelDiv = styled.div<ButtonProps>`
    display: flex;
    height: 40px;
    border-radius: ${borderRadius};
    background: ${({background}) => (background ? background : primaryColor)};
    box-shadow: ${cardShadow};

    ${({dropdown}) =>
            dropdown !== true
                    ? `width: 140;`
                    : `width: 173px;`}

    ${media.mobile} {
        width: 80px;
        height: 30px;
        text-align: center;
        vertical-align: center;
    }

    ${media.tablet} {
        width: 80px;
        height: 30px;
        text-align: center;
        vertical-align: center;
    }
`;

export const ActionButton = styled.button<{ variant?: "edit" | "delete" | "disabled" }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 6px 14px;
    font-size: 14px;
    font-weight: 500;
    font-family: ${fontFamily};
    border-radius: ${borderRadius};
    border: 1px solid ${borderColor};
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    background-color: ${cardBackground};

    ${({variant}) =>
            variant === "edit" &&
            css`
                background-color: rgba(43, 127, 255, 0.08);
                color: ${primaryColor};
                border-color: rgba(43, 127, 255, 0.3);

                &:hover {
                    background-color: rgba(43, 127, 255, 0.14);
                    border-color: ${primaryColor};
                }
            `}

    ${({variant}) =>
            variant === "delete" &&
            css`
                background-color: var(--danger-subtle);
                color: var(--destructive);
                border-color: var(--border);

                &:hover {
                    background-color: var(--danger-subtle);
                    border-color: var(--destructive);
                }
            `}

    ${({variant}) =>
            variant === "disabled" &&
            css`
                background-color: var(--muted);
                color: var(--muted-foreground);
                border-color: ${borderColor};
                cursor: not-allowed;

                &:hover {
                    background-color: var(--muted);
                    border-color: ${borderColor};
                }
            `}
`;
